import React, { useState } from 'react';
import AnimatedSlideshow from './AnimatedSlideshow';
import SectionModal, { ModalContent } from './SectionModal';
import FinancialTable from './FinancialTable';
import KeySlideshow from './NFTSlideshow';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Team data
const teamMembers = [
  {
    name: "Aaron Carvajal",
    role: "DEVELOPER",
    image: "/images/Aaron-Team.png",
    description: "Aaron has 28 years of innovating: 20 years in technology, 8 years in real estate as a former real estate broker. Founder of Future of the Free, US Hemp Co and many social impact ventures.",
    link: "https://www.linkedin.com/in/aaron-rene-carvajal-8bb00a38",
    linkText: "LinkedIn"
  },
  {
    name: "Kate Yurenda M.S.",
    role: "COMMUNITY DEVELOPMENT",
    image: "/images/Kate_Yurenda-MS.png",
    description: "Kate is the founder of Dream Bold Network, director of 'Manifest Peace', Qi Gong instructor and energy healer—that is dedicated to executing her vision.",
    link: "https://dreamboldnetwork.com",
    linkText: "Dream Bold Network"
  },
  {
    name: "Gabriel Cardona",
    role: "ADVISOR, DEVELOPER",
    image: "/images/Gabriel-Cardona.png",
    description: "Technology evangelist and builder in developer relations and Web3. Former Developer Evangelist at Ava Labs; co-host of the Subnet Show. Contributed to W3C's HTML Working Group; built BITBOX, acquired by Bitcoin.com."
  },
  {
    name: "Richard Cedano",
    role: "SOUTH AMERICA COORDINATOR",
    image: "/images/Richard_Cedano.png",
    description: "Fifteen years hosting travelers from all over the globe as a Surf Tourism entrepreneur. An Eco Village visionary with building and management experience."
  },
  {
    name: "Thomas Ausmus",
    role: "PROJECT MANAGER",
    image: "/images/Thomas-Ausmus.png",
    description: "Supply chain operations management. Operations leader with over 25 years supervision and management of disparate timers. Union and Non-Union employees, within multiple industries"
  },
  {
    name: "Maria Reyes",
    role: "MARKETING",
    image: "/images/Maria-Lotretta.png",
    description: "Expert in all things internet and spreading the word. A natural inclination to get excited and inspire movement. Twenty years management experience."
  },
  {
    name: "Saiteju Surapaneni",
    role: "DATA SECURITY / EAST ASIA COORDINATOR",
    image: "/images/Saiteju_Surapaneni-bw.png",
    description: "Well versed with a MS in Information Technology, and a MS in Data Security. Saiteju is a technical wizard and long time team member."
  },
  {
    name: "Steve Lozano",
    role: "GLOBAL PROJECT ADVISOR",
    image: "/images/Steve_Lozano.png",
    description: "Steve is a real estate expert who has vast experience traveling and living abroad, and a real world knowledge of what it takes to buy real estate. ",
    link: "https://linkedin.com/in/steven-lozano-b14165b",
    linkText: "LinkedIn"
  },
  {
    name: "Mike Hefling",
    role: "MEDIA/EUROPEAN COORDINATOR",
    image: "/images/Mike_Hefling.png",
    description: "Mike brings decades of experience coordinating business and trade in Europe, Canada, and Japan. Superb skills at making and nurturing connections abroad."
  }
];

// Team Modal Component
const TeamModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-2 sm:p-4 animate-fade-in"
      style={{ zIndex: 99999, paddingTop: '140px' }}
      onClick={onClose}
    >
      <div 
        className="bg-gray-800 text-gray-300 rounded-lg shadow-xl w-full max-w-7xl max-h-[80vh] overflow-y-auto relative"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button - fixed to viewport */}
        <button
          onClick={onClose}
          className="fixed top-32 right-4 z-[100000] text-white hover:text-red-500 transition-colors"
          aria-label="Close modal"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="p-4 sm:p-6 md:p-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8 text-center text-blue-300 pt-2">Meet the Seed Team</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {teamMembers.map((member, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-6 text-center">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                  />
                  <div className="text-xs text-blue-300 uppercase tracking-wide mb-1">
                    {member.role}
                  </div>
                  <h3 className="text-lg font-bold text-gray-300 mb-3">
                    {member.name}
                  </h3>
                  <p className="text-sm text-gray-300 leading-relaxed mb-4">
                    {member.description}
                  </p>
                  {member.link && (
                    <a 
                      href={member.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-400 hover:text-blue-300 text-sm"
                    >
                      {member.linkText}
                    </a>
                  )}
                </div>
              ))}
            </div>
        </div>
      </div>
    </div>
  );
};


// Genesis Trilogy financial data
const genesisTrilogyFinancials = {
  units: '8',
  daoNights: '165 × 8 = 1320',
  memberNights: '200 × 8 = 1600',
  pricePerNight: '$60',
  creditsSold: '880,000 (550/night)',
  grossPotential: '$79,200',
  creditPrice: '$1.00',
  occupancy40: '$31,680',
  totalRaised: '$880,000',
  reserveYield: '$6,000',
  purchasePrice: '$580,000',
  totalCoverage: '$37,680',
  legalAdmin: '$20,000',
  estimatedOps: '$35,040 ($12/day)',
  reserveFund: '$120,000 (20.7%) + $160,000 Project',
  surplus: '~$1,880'
};

// Genesis Funding text component
const GenesisFundingSection: React.FC<{ onInfoClick?: () => void }> = ({ onInfoClick }) => (
  <div className="mt-8 space-y-4">
    {/* Genesis Trilogy Header */}
    <div className="text-center mb-6">
      <h3 className="text-2xl font-bold text-gray-300 mb-2">Genesis Trilogy: Punta Sal, Peru</h3>
      <a href="https://bornfree.io" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-lg">
        bornfree.io
      </a>
    </div>
    
    <FinancialTable data={genesisTrilogyFinancials} showCalculationExplanation={false} onInfoClick={onInfoClick} />
    
    <div className="text-sm text-gray-400 leading-relaxed space-y-3">
      <h4 className="font-bold text-gray-300 text-base">Genesis Funding</h4>
      
      <p>
        The Genesis Trilogy Key comes to life by breaking ground on a geodesic dome project while partnering with Caesar's Soul Surf Trip to provide logistics and hands-on support for the DAO. Participating in the release of the Keep It Real film helps start our collective movement. Together, these efforts lay the foundation for something bigger than a single project.
      </p>
      
      <p>
        In northern Peru, we're establishing a local dome-building workshop—a place where prototypes are crafted, builders are trained, and innovation is sparked. This hub will serve as both our home base and a model for how dome communities can take root around the world. Every structure raised here is not just shelter, but a step toward a replicable blueprint for sustainable living and collective growth.
      </p>
      
      <p>
        To make this vision sustainable, we've adjusted our Stength In Numbers calculator nightly operating costs from $12 to $15 which increses the reserve. This is balanced by a $100 p/night launch adjustment, generating $160,000 to begin the DAO expansion. By raising the operations costs to $15 a day per room we get surplus reserves to start a global community-building endeavor. Everyone benefits through discounted adventures and tours for members at our first base of operations.
      </p>
      
      <p>
        As we open this new chapter, the Dream Bold Network adds the cultural heartbeat—launching with a Genesis Celebration that gathers members from day one. And as a special benefit, this Key also unlocks early access to the pre-release of Keep It Real, a project designed to inspire events, deepen connections, and set the stage for the community we are building together.
      </p>
    </div>
  </div>
);

const getSections = (onInfoClick: () => void): { [key: string]: ModalContent } => ({
  property: {
    title: 'Property',
    main: "The Experience Key is the foundation of our community—because every journey needs a place to begin. As physical beings, we are grounded by the Earth, and land is where our shared story takes root. Each Experience Key represents not just shelter, but a destination worth exploring. The Genesis location anchors us in the lush Amazon rainforest of Peru, where life flourishes and our first base of operations is taking shape.",
    details: (
      <p>We'll launch in Punta Sal, Peru—an oceanfront region known for surf, sun, and vibrant local culture. By securing a compact property here, we'll be able to refine our model, build local trust, and showcase what Born Free can do.</p>
    ),
    media: [
      '/images/punta-sal/punta-sal-01.webp',
      '/images/punta-sal/punta-sal-02.webp',
      '/images/punta-sal/punta-sal-03.webp',
      '/images/punta-sal/punta-sal-04.webp',
      '/images/punta-sal/punta-sal-05.webp',
      '/images/punta-sal/punta-sal-06.webp',
      '/images/punta-sal/punta-sal-07.webp',
      '/images/punta-sal/punta-sal-08.webp',
      '/images/punta-sal/punta-sal-09.webp',
      '/images/punta-sal/punta-sal-10.webp',
      '/images/punta-sal/punta-sal-11.webp',
      '/images/punta-sal/punta-sal-12.webp',
      '/images/punta-sal/punta-sal-13.webp',
      '/images/punta-sal/punta-sal-14.webp',
      '/images/punta-sal/punta-sal-15.webp',
      '/images/punta-sal/punta-sal-16.webp',
      '/images/punta-sal/punta-sal-17.webp',
      '/images/punta-sal/punta-sal-18.webp',
      '/images/punta-sal/punta-sal-19.webp',
      '/images/punta-sal/punta-sal-20.webp',
      '/images/punta-sal/punta-sal-21.webp',
      '/images/punta-sal/punta-sal-22.webp',
      '/images/punta-sal/punta-sal-23.webp',
      '/images/punta-sal/punta-sal-24.webp',
      '/images/punta-sal/punta-sal-25.webp',
      '/images/punta-sal/punta-sal-26.webp',
      '/images/punta-sal/punta-sal-27.webp',
      '/images/punta-sal/punta-sal-28.webp',
      '/images/punta-sal/punta-sal-29.webp',
      '/images/punta-sal/punta-sal-30.webp',
      '/images/punta-sal/punta-sal-31.webp',
      '/images/punta-sal/punta-sal-32.webp',
      '/images/punta-sal/punta-sal-33.webp',
      '/images/punta-sal/punta-sal-34.webp',
      '/images/punta-sal/punta-sal-35.webp',
      '/images/punta-sal/punta-sal-36.webp',
      '/images/punta-sal/punta-sal-37.webp',
      '/images/punta-sal/punta-sal-38.webp',
      '/images/punta-sal/punta-sal-39.webp',
      '/images/punta-sal/punta-sal-40.webp',
      '/images/punta-sal/punta-sal-41.webp',
      '/images/punta-sal/punta-sal-42.webp',
      '/images/punta-sal/punta-sal-43.webp',
      '/images/punta-sal/punta-sal-44.webp',
      '/images/punta-sal/punta-sal-45.webp',
      '/images/punta-sal/punta-sal-46.webp',
      '/images/punta-sal/punta-sal-47.webp'
    ],
    footerContent: (
      <>
        <div className="text-sm text-gray-300 leading-relaxed mb-6">
          <h4 className="font-bold text-gray-300 text-base mb-2">Genesis Trilogy: Punta Sal, Peru</h4>
          <a href="https://bornfree.io" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">bornfree.io</a>
          <p>The Smiling Crab</p>
          <p>1187 Balneario de Punta Sal Grande</p>
          <p>Balneario de Punta Sal, Tumbes 01 Peru</p>
          <p>8 Rooms, $580,000</p>
          <a href="https://www.adondevivir.com/propiedades/clasificado/veclhtin-oportunidad-hotel-en-venta-en-mancora-y-totalmente-145416929.html" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">See Listing</a>
        </div>
        <GenesisFundingSection onInfoClick={onInfoClick} />
      </>
    )
  },
  project: {
    title: 'Project',
    main: "Creation is at the heart of human progress. The Project Key represents innovation—building better systems, tools, and solutions. As the second piece of the Genesis Trilogy, this Key showcases a sustainable dwelling built rapidly and affordably, offering a real-world model for decentralized, eco-conscious living. It expands on the Genesis property, demonstrating how we can live with the land, not just on it.",
    details: (
      <>
        <p>This project will expand the Genesis initiative by adding a prototype site and an innovation hub—establishing a proof of concept that dome homes can be built affordably and efficiently by anyone. The dome will be integrated into the existing coastal property or at a nearby lot to serve as a base of operations in Peru and expand adventure offerings. Equipment and vans will be purchased for transporting materials and repurposed as part of a "Coastal Roadtrip Adventure" in partnership with Caesar's Soul Surf Trip, connecting Lima to Máncora. The experience and logistics from this prototype project will lay the groundwork for developing full dome villages on future Born Free properties.</p>
        <div>
          <h4 className="font-bold text-gray-300">Future of the Free</h4>
          <p>Our California-based dome building partner will help design and provide guidance for the development of our DAO innovation hub. This will demonstrate how geodesic domes are resilient, easy to build, and part of an emerging template for freedom.</p>
          <a href="https://futureofthefree.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">futureofthefree.com</a>
        </div>
        <div>
          <h4 className="font-bold text-gray-300">Caesar's Soul Surf Trip</h4>
          <p>Our Peruvian partner brings deep local insight and passion for clean oceans. Their surf-guided experience will help us integrate with the land and people while promoting environmental stewardship.</p>
          <a href="https://caesarssoulsurf.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">caesarssoulsurf.com</a>
        </div>
      </>
    ),
    media: [
      '/images/Porject-2.jpeg',
      '/images/Project-1.jpeg',
      '/images/Project-1.png',
      '/images/Project-3.jpeg',
      '/images/Project-4.jpeg',
      '/images/Purpose-1.png',
      '/images/Project-dome-1.jpg',
      '/images/Project-dome-2.jpg',
      '/images/Project-dome-vanlife.jpeg',
      { type: 'mp4', src: '/images/IMG_2294.mov' },
      { type: 'youtube', src: 'https://youtu.be/_2ZKxTx8h2Y' },
      { type: 'youtube', src: 'https://youtu.be/5YvaqJCrf20' },
      { type: 'youtube', src: 'https://youtu.be/tEg3AIk0wPI' },
      { type: 'youtube', src: 'https://youtu.be/byBTdWE-ytk' },
      { type: 'youtube', src: 'https://youtu.be/s6nKd-tZZYQ' }
    ],
    footerContent: (
      <GenesisFundingSection onInfoClick={onInfoClick} />
    )
  },
  purpose: {
    title: 'Purpose',
    main: (
      <>
        Purpose is what moves a community from vision to momentum. The third Key in the Genesis Trilogy serves as a catalyst—highlighting the power of media, storytelling, and creative collaboration. This Key includes a pre-release of the film <em>Keep It Real</em>, and introduces new paths for airdrops, collective engagement, and decentralized media distribution. It's the spark that brings everything—and everyone—into motion.
      </>
    ),
    details: (
       <>
        <p>Produced by Dream Bold Network, the docuseries explores themes like healing, consciousness, and the power of Qi Gong—an ancient, meditative movement practice. The Key gives holders early access and influence on future creative releases and events.</p>
        <p>Dream Bold Network is a team of people that are dedicated to uniting our world through film production, mindset coaching, creative media, dynamic healing practices, artistic expression and bold leadership, helping people realize their innate superhuman powers.</p>
        <a href="https://dreamboldnetwork.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">dreamboldnetwork.com</a>
      </>
    ),
    media: [
      '/images/9CAF1186-CC8D-4B0D-9DDF-7CB1A1EFEBEE.jpeg',
      '/images/Keep-It-Real-Movie.avif',
      { type: 'youtube', src: 'https://youtu.be/KgB0w2ITh7M' },
      { type: 'youtube', src: 'https://youtu.be/dX9ScaKEbDg' },
      { type: 'youtube', src: 'https://youtu.be/bIba8uGlv6w' }
    ],
    footerContent: (
      <GenesisFundingSection onInfoClick={onInfoClick} />
    )
  }
});

interface CommunityProps {
  isTeamModalOpen: boolean;
  setIsTeamModalOpen: (open: boolean) => void;
}

const Community: React.FC<CommunityProps> = ({ isTeamModalOpen, setIsTeamModalOpen }) => {
  const [modalContent, setModalContent] = useState<ModalContent | null>(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const sections = getSections(() => setIsInfoModalOpen(true));

  const SeeMoreButton = ({ section }: { section: 'property' | 'project' | 'purpose' }) => (
    <button
      onClick={() => setModalContent(sections[section])}
      className="text-blue-400 cursor-pointer hover:text-blue-300 mt-4 inline-block"
    >
      See More
    </button>
  );

  return (
    <>
      <section 
        id="community" 
        className="bg-gray-900 text-gray-300 pb-20" 
        style={{ paddingTop: '4rem', scrollMarginTop: '120px' }}
      >
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-y-8 gap-x-12 items-start max-w-7xl mx-auto">
            {/* Left Column */}
            <div className="space-y-4 text-left">
              <h3 className="text-2xl sm:text-3xl font-bold">Join the Genesis Project</h3>
              <p className="text-gray-400 w-full text-sm sm:text-base" style={{ lineHeight: '1.5' }}>Born Free is a shared network guided by a Decentralized Autonomous Organization (DAO), built to be powered by its members from the very beginning. Its purpose is to foster a committed, collaborative community that drives both the platform's growth and long-term success. Building and sustaining that strong, engaged community is the first step—and the ongoing mission.</p>
              <p className="text-gray-400 w-full text-sm sm:text-base" style={{ lineHeight: '1.5' }}>
                Born Free is not your typical project created to pump price. There are no whitelists or private sales. No small group gets special ownership or pre-launch preference. Keys are used as living art that put forth action in the real world.
              </p>
              <p className="text-gray-400 w-full text-sm sm:text-base" style={{ lineHeight: '1.5' }}>
                Genesis Project Keys will be available to those who step forward to bravely pursue this important and ambitious endeavor. Purchasers of the very first Key offering will also receive a limited Born Free Geneses Project Key.
              </p>
              
              {/* Meet the Seed Team Link */}
              <div className="pt-4 pb-6 md:pb-0">
                <button
                  onClick={() => setIsTeamModalOpen(true)}
                  className="text-bornfree-600 hover:text-blue-300 font-semibold underline text-sm sm:text-base"
                >
                  MEET THE SEED TEAM
                </button>
              </div>
            </div>

            {/* Right Column */}
            <div className="flex justify-center items-center h-full mt-4 md:mt-0">
              <AnimatedSlideshow />
            </div>
          </div>
        </div>

        {/* Genesis Trilogy Section */}
        <div 
          className="py-16 mt-32 sm:mt-20 relative" 
          style={{ 
            backgroundImage: 'url(/images/fractals-3248243_1920.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-75"></div>
          <div className="container mx-auto px-6 text-center relative z-10">
            <h2 className="font-bold text-3xl sm:text-4xl md:text-5xl mb-6 sm:mb-8">
              Genesis Trilogy
            </h2>
            <p className="text-gray-400 mt-4 max-w-3xl mx-auto text-sm sm:text-base" style={{ lineHeight: '1.5' }}>
              The Genesis Trilogy marks the beginning of the Born Free journey—introducing the first three Key types and showing how they interact, complement, and empower one another. These initial projects are united by place, time, and purpose, demonstrating how community-led initiatives can be structured to solve real-world challenges in creative and decentralized ways. This is just the first chapter in a much larger story—where every experience unlocks new potential. As we enter the age of blockchain and AI, it's time to design and deliver smarter, more collaborative solutions.
            </p>

            {/* Genesis Set Section */}
            <div className="mt-16 sm:mt-20 text-center">
              <h3 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6 text-bornfree-600">Genesis Set</h3>
              <p className="text-base sm:text-lg text-blue-300 mb-10 sm:mb-12">A single forge, three powerful Keys—united by time, place, and mission.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mt-12 text-left">
              <div>
                <h3 className="font-bold text-center" style={{ fontSize: '1.6875rem' }}>Property</h3>
                <p className="text-gray-400 mt-4" style={{ lineHeight: '1.25' }}>
                  {sections.property.main}
                </p>
                <SeeMoreButton section="property" />
              </div>
              <div>
                <h3 className="font-bold text-center" style={{ fontSize: '1.6875rem' }}>Project</h3>
                <p className="text-gray-400 mt-4" style={{ lineHeight: '1.25' }}>
                  {sections.project.main}
                </p>
                <SeeMoreButton section="project" />
              </div>
              <div>
                <h3 className="font-bold text-center" style={{ fontSize: '1.6875rem' }}>Purpose</h3>
                <p className="text-gray-400 mt-4" style={{ lineHeight: '1.25' }}>
                  {sections.purpose.main}
                </p>
                <SeeMoreButton section="purpose" />
              </div>
            </div>

            {/* Art with Purpose Section */}
            <div className="mt-16 grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
              {/* Left Column - Text (1/4) */}
              <div className="lg:col-span-1 space-y-3 pr-6 pt-8">
                <h2 className="text-4xl font-bold mb-2 text-blue-300 text-left">stART</h2>
                
                <div className="space-y-1 text-gray-400 text-left" style={{ lineHeight: '1.15', fontFamily: 'inherit' }}>
                  <p>
                    Art here means action. created by individuals, shaped by collective imagination, and led by a clear vision for a world at a turning point.
                  </p>
                  
                  <p>
                    Each Key made is a ripple in the real world—fueling projects, shaping experiences, and helping communities grow. In this way, art becomes a vessel for change.
                  </p>
                  
                  <p className="font-bold text-bornfree-600 text-lg pt-4">
                    WRITE A NEW STORY.
                  </p>
                  
                  <p>
                    The blockchain era marks the dawn of a new chapter in human connection that will reshape this generation. Web3 is our chance to reimagine how we live, create, and share.
                  </p>
                  
                  <p>
                    We invite you to help shape this bold decentralized future.
                  </p>
                </div>
              </div>
              
              {/* Right Column - Slideshow (3/4) */}
              <div className="lg:col-span-3 -mx-6">
                <div className="relative w-full">
                  <KeySlideshow />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <SectionModal content={modalContent} onClose={() => setModalContent(null)} />
      <TeamModal isOpen={isTeamModalOpen} onClose={() => setIsTeamModalOpen(false)} />
      
      {/* Calculator Key Modal */}
      {isInfoModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
          onClick={() => setIsInfoModalOpen(false)}
        >
          <div 
            className="bg-gray-800 text-gray-300 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-8 relative">
              <button
                onClick={() => setIsInfoModalOpen(false)}
                className="absolute top-6 right-6 text-gray-400 hover:text-white"
              >
                <XMarkIcon className="h-8 w-8" />
              </button>
              
              <h2 className="text-3xl font-bold mb-6 text-center text-blue-300">Calculator Key</h2>
              
              <div className="space-y-6 text-gray-300">
                <div>
                  <h3 className="text-xl font-bold text-gray-300 mb-2">Property Details</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-semibold text-blue-300">Units:</span> Number of rentable units in the property</p>
                    <p><span className="font-semibold text-blue-300">Nights for Members:</span> Total available nights per year for DAO members (200 nights × units)</p>
                    <p><span className="font-semibold text-blue-300">Credits Sold:</span> Total credits generated from member nights (nights × credits per night)</p>
                    <p><span className="font-semibold text-blue-300">Credit Price:</span> Fixed at $1.00 per credit</p>
                    <p><span className="font-semibold text-blue-300">Total Raised:</span> Credits sold × credit price</p>
                    <p><span className="font-semibold text-blue-300">Purchase Price:</span> Property acquisition cost</p>
                    <p><span className="font-semibold text-blue-300">Legal/Admin:</span> Standard $20,000 for legal and administrative setup</p>
                    <p><span className="font-semibold text-blue-300">Reserve Fund:</span> Minimum operating capital (calculated as annual operating costs)</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-300 mb-2">Annual DAO Income</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-semibold text-blue-300">DAO Nights:</span> Remaining nights available for public rental (365 - 200) × units</p>
                    <p><span className="font-semibold text-blue-300">Price per Night:</span> Market rate for public rentals</p>
                    <p><span className="font-semibold text-blue-300">Gross Potential:</span> DAO nights × price per night</p>
                    <p><span className="font-semibold text-blue-300">40% Occupancy:</span> Conservative estimate of actual rental income</p>
                    <p><span className="font-semibold text-blue-300">Reserve Yield (5%):</span> Annual return on reserve fund investment</p>
                    <p><span className="font-semibold text-blue-300">Total Coverage:</span> Combined DAO income from rentals and reserve yield</p>
                    <p><span className="font-semibold text-blue-300">Estimated Ops Cost:</span> Annual operating expenses (utilities, maintenance, cleaning, etc.)</p>
                    <p><span className="font-semibold text-blue-300">Surplus:</span> Total coverage minus estimated operating costs</p>
                  </div>
                </div>
                
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-bold text-yellow-300 mb-2">How It Works</h3>
                  <p className="text-sm">
                    The calculator automatically determines the required "credits per night" to make each project financially sustainable. 
                    It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, 
                    legal/admin fees, and a minimum reserve—while also generating a positive projected surplus.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Community; 